/**
 * The emit client. Typed from the registry, and deliberately hard to misuse.
 *
 * CONFIDENTIAL — ASI Internal.
 * Copyright 2026, Partnership Net; Agency for Sponsorship Intelligence, Lda;
 * Advanced Sponsorship Insights, GmbH. Confidential and proprietary.
 *
 * FOUR THINGS THIS REFUSES TO DO, each of which is cheap here and expensive to retrofit:
 *
 *   1. It never emits a Tier A event. Billing truth is never client-asserted — a browser
 *      can be tampered with, and a disputed invoice has to be defensible. `signal()` only
 *      accepts Tier B codes, at the type level and again at run time.
 *   2. It never sends a property an event did not declare. Anything outside the
 *      allow-list is dropped before it reaches the sink, so a future transport cannot
 *      carry a field nobody reviewed.
 *   3. It never touches storage. No cookie, no localStorage, no device fingerprint —
 *      which is what keeps terminal-equipment consent rules out of the picture entirely
 *      until in-product consent exists.
 *   4. It never emits at all until the tenant is contractually gated in. Until a tenant's
 *      contract carries the service-usage clause, that tenant is Tier A only, and the
 *      gate is a flag checked on the emit path — not a note in a document.
 *
 * There is no transport yet. Events land in a bounded memory sink that the app can read
 * and nothing can exfiltrate. When the governed access layer arrives it replaces the
 * sink, and no caller changes.
 */

import { EVENTS, type EventCode, type EventDef, type TierBCode } from './registry';

/**
 * Who produced an event, at the only granularity anyone needs.
 *
 * `internal` is ASI's own people. `external` is everyone else. `unknown` is the honest
 * answer before an identity exists, which is where this product sits today.
 *
 * It lives on the ENVELOPE rather than in an event's properties for two reasons: it
 * applies to every event, so making each one declare it would be thirteen chances to
 * forget; and it must never be strippable by the property allow-list, because filtering
 * our own traffic out of real usage is the whole point of collecting it early.
 */
export type ActorClass = 'internal' | 'external' | 'unknown';

export interface TelemetryEvent {
  readonly code: EventCode;
  /** Event time, not ingest time — the meter bills on when it happened. */
  readonly at: string;
  readonly actorClass: ActorClass;
  readonly properties: Readonly<Record<string, string | number | boolean>>;
}

export interface TelemetryConsent {
  /** The tenant's contract carries the service-usage controller clause. */
  readonly tenantClause: boolean;
  /** The person has accepted in-product analytics. */
  readonly userConsent: boolean;
}

/** Nothing is on until someone turns it on. Off is the safe default and the honest one. */
let consent: TelemetryConsent = { tenantClause: false, userConsent: false };

/**
 * Internal domains. Anyone signing in from one of these is us.
 *
 * Kept here rather than in configuration because it is a fact about the company, not a
 * deployment setting, and because a mis-set env var would silently reclassify real users
 * as staff and quietly delete them from the numbers.
 */
const INTERNAL_DOMAINS: readonly string[] = [
  'partnership-net.com',
  'advanced-sponsorship-insights.com',
];

/**
 * Classify an email address WITHOUT keeping it.
 *
 * This is the only function in the client that ever sees an address, and it returns a
 * three-valued enum. The address is not stored, not returned and not attached to
 * anything — which is what lets internal traffic be filtered out of the numbers while
 * the denylist on personal data stays absolute.
 */
export function classifyActor(email: string | null | undefined): ActorClass {
  if (typeof email !== 'string') return 'unknown';
  const at = email.lastIndexOf('@');
  if (at < 0) return 'unknown';
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  if (domain === '') return 'unknown';
  return INTERNAL_DOMAINS.includes(domain) ? 'internal' : 'external';
}

/** Unknown until an identity layer says otherwise. Today it never does. */
let actorClass: ActorClass = 'unknown';

/**
 * Set the actor class for this session. Call it once, at the identity boundary.
 *
 * It takes the CLASS, not the address: whoever holds the identity calls `classifyActor`
 * and hands over the result, so the address never travels further into the application
 * than the place that already had it.
 */
export function setActorClass(next: ActorClass): void {
  actorClass = next;
}

export const currentActorClass = (): ActorClass => actorClass;

export function setTelemetryConsent(next: TelemetryConsent): void {
  consent = next;
}

export const telemetryEnabled = (): boolean => consent.tenantClause && consent.userConsent;

/** Bounded on purpose: a sink that grows without limit is a memory leak with a nice name. */
const MAX_EVENTS = 500;
let sink: TelemetryEvent[] = [];

export const readTelemetry = (): readonly TelemetryEvent[] => sink;
export const clearTelemetry = (): void => {
  sink = [];
};

type Value = string | number | boolean;

/**
 * Emit a Tier B signal.
 *
 * `code` is narrowed to the Tier B codes, so a Tier A meter does not compile here. The
 * run-time check behind it is not redundant: JavaScript reaches this function from places
 * TypeScript does not check, and the rule is worth enforcing twice.
 */
export function signal(code: TierBCode, properties: Readonly<Record<string, Value>> = {}): void {
  // The lookup is widened on purpose: TypeScript already proves `code` is Tier B, and the
  // guard below exists for the JavaScript callers it never sees. Narrowing here would let
  // the compiler delete the only check that catches them.
  const def: EventDef = EVENTS[code];
  if (def.tier !== 'B') {
    throw new Error(
      `[telemetry] ${code} is a Tier A meter. The browser never emits one — it is emitted server-side, from ${def.emission}.`,
    );
  }
  if (!telemetryEnabled()) return;

  const allowed = new Set<string>(def.properties);
  const clean: Record<string, Value> = {};
  for (const [k, v] of Object.entries(properties)) {
    // Silently dropping would hide a mistake; the registry is the contract, so a property
    // nobody declared is a bug in the caller, and it is named where a developer sees it.
    if (!allowed.has(k)) {
      console.warn(`[telemetry] ${code}: property "${k}" is not on its allow-list — dropped.`);
      continue;
    }
    clean[k] = v;
  }

  sink = [...sink, { code, at: new Date().toISOString(), actorClass, properties: clean }].slice(
    -MAX_EVENTS,
  );
}

/**
 * What a Tier A meter WOULD carry, for a server call to send.
 *
 * Deliberately not an emitter. It exists so a commit surface can be written against the
 * meter today — same allow-list, same code — and so the day the endpoint lands the call
 * site does not have to be rediscovered.
 */
export function meterPayload(
  code: EventCode,
  properties: Readonly<Record<string, Value>>,
): { readonly code: EventCode; readonly properties: Readonly<Record<string, Value>> } {
  const def = EVENTS[code];
  const allowed = new Set<string>(def.properties);
  const clean: Record<string, Value> = {};
  for (const [k, v] of Object.entries(properties)) if (allowed.has(k)) clean[k] = v;
  return { code, properties: clean };
}
